import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ChevronRight, HandHelping, Inbox, Send } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { formatDateTime, formatRelative } from '@/lib/formatters';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Textarea';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import { TicketStatusChip } from '@/components/tickets/TicketChips';
import { describeWorkError } from '@/features/workMode/api';
import type { WorkSession } from '@/features/workMode/types';
import {
  useWorkTicketComment,
  useWorkTicketThread,
  useWorkTickets,
  type WorkTicketSummary,
  type WorkTicketThread,
} from '@/features/tickets/hooks';
import {
  TICKET_STATUS_META,
  describeTicketEvent,
  isTicketClosed,
  ticketCategoryIcon,
} from '@/features/tickets/model';
import type { TicketStatus } from '@/types/database';

interface WorkTicketsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: WorkSession;
  onNewRequest: () => void;
}

/**
 * "Requests" on the bench: everything this worker has asked for, with
 * the status the office has set, and the thread behind each one so they
 * can answer "which size?" without leaving the station.
 */
export function WorkTicketsSheet({ open, onOpenChange, session, onNewRequest }: WorkTicketsSheetProps) {
  const ticketsQ = useWorkTickets(session.id, open);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) setSelectedId(null);
  }, [open]);

  const tickets = ticketsQ.data ?? [];
  const openOnes = tickets.filter((t) => !isTicketClosed(t.status));
  const closedOnes = tickets.filter((t) => isTicketClosed(t.status));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {selectedId ? (
          <ThreadView
            session={session}
            ticketId={selectedId}
            summary={tickets.find((t) => t.id === selectedId) ?? null}
            onBack={() => setSelectedId(null)}
          />
        ) : (
          <>
            <SheetHeader className="pr-12">
              <SheetTitle className="flex items-center gap-2">
                <HandHelping className="h-5 w-5 text-info" />
                My requests
              </SheetTitle>
              <SheetDescription>
                What you have asked for and where it is. Tap one to read the thread or reply.
              </SheetDescription>
            </SheetHeader>
            <SheetBody className="space-y-5">
              <Button
                size="lg"
                className="h-12 w-full text-base"
                onClick={() => {
                  onOpenChange(false);
                  onNewRequest();
                }}
              >
                <HandHelping className="h-5 w-5" />
                New request
              </Button>

              {ticketsQ.isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-20 w-full rounded-xl" />
                  <Skeleton className="h-20 w-full rounded-xl" />
                </div>
              ) : ticketsQ.error ? (
                <p className="rounded-xl border border-fresh-salmon/60 bg-fresh-salmon/10 px-4 py-3 text-sm text-fresh-salmon-dark">
                  {describeWorkError(ticketsQ.error)}
                </p>
              ) : tickets.length === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border px-4 py-10 text-center">
                  <Inbox className="h-6 w-6 text-muted-foreground" />
                  <p className="text-base font-medium text-foreground">Nothing asked for yet</p>
                  <p className="max-w-xs text-sm text-muted-foreground">
                    A tool, a part, a repair, an idea — file it here and you will see when it is
                    picked up, ordered and done.
                  </p>
                </div>
              ) : (
                <>
                  {openOnes.length > 0 && (
                    <TicketGroup title="Open" tickets={openOnes} onSelect={setSelectedId} />
                  )}
                  {closedOnes.length > 0 && (
                    <TicketGroup
                      title="Answered recently"
                      tickets={closedOnes}
                      onSelect={setSelectedId}
                      dimmed
                    />
                  )}
                </>
              )}
            </SheetBody>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function TicketGroup({
  title,
  tickets,
  onSelect,
  dimmed,
}: {
  title: string;
  tickets: WorkTicketSummary[];
  onSelect: (id: string) => void;
  dimmed?: boolean;
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
        {title} <span className="text-foreground/70">({tickets.length})</span>
      </h3>
      <ul className="space-y-2">
        {tickets.map((t) => {
          const Icon = ticketCategoryIcon(t.category_slug);
          return (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => onSelect(t.id)}
                className={cn(
                  'flex w-full items-start gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-secondary/60 active:bg-secondary',
                  dimmed && 'opacity-70',
                )}
              >
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/60 text-muted-foreground">
                  <Icon className="h-4 w-4" strokeWidth={1.7} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-mono text-xs text-muted-foreground">{t.ref}</span>
                    <TicketStatusChip status={t.status} />
                    {(t.priority === 'high' || t.priority === 'urgent') && (
                      <span className="rounded-full bg-fresh-salmon/30 px-2 py-0.5 text-xs font-medium text-fresh-salmon-dark">
                        {t.priority === 'urgent' ? 'Urgent' : 'Holding you up'}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 truncate text-base font-medium text-foreground">{t.title}</p>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {isTicketClosed(t.status) && t.resolution
                      ? t.resolution
                      : t.last_comment
                        ? `${t.last_comment.author_name}: ${t.last_comment.body}`
                        : t.assignee_name
                          ? `${t.assignee_name} has it`
                          : TICKET_STATUS_META[t.status].hint}
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {formatRelative(t.last_activity_at)}
                    {t.comment_count > 0 && ` · ${t.comment_count} ${t.comment_count === 1 ? 'reply' : 'replies'}`}
                  </p>
                </div>
                <ChevronRight className="mt-2 h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

type ThreadItem =
  | { kind: 'event'; at: string; event: WorkTicketThread['events'][number] }
  | { kind: 'comment'; at: string; comment: WorkTicketThread['comments'][number] };

function ThreadView({
  session,
  ticketId,
  summary,
  onBack,
}: {
  session: WorkSession;
  ticketId: string;
  summary: WorkTicketSummary | null;
  onBack: () => void;
}) {
  const threadQ = useWorkTicketThread(session.id, ticketId);
  const reply = useWorkTicketComment();
  const [body, setBody] = useState('');

  const thread = threadQ.data ?? null;
  const ticket = thread?.ticket ?? null;
  const status: TicketStatus | null = ticket?.status ?? summary?.status ?? null;

  const items = useMemo<ThreadItem[]>(() => {
    if (!thread) return [];
    const out: ThreadItem[] = [];
    for (const e of thread.events) out.push({ kind: 'event', at: e.created_at, event: e });
    for (const c of thread.comments) out.push({ kind: 'comment', at: c.created_at, comment: c });
    out.sort((a, b) => a.at.localeCompare(b.at));
    return out;
  }, [thread]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    try {
      await reply.mutateAsync({ sessionId: session.id, ticketId, body: text });
      setBody('');
    } catch (e) {
      toast.error(describeWorkError(e));
    }
  };

  return (
    <>
      <SheetHeader className="gap-2 pr-12">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex min-h-[44px] items-center gap-1.5 self-start text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> All requests
        </button>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="font-mono">{ticket?.ref ?? summary?.ref ?? ''}</span>
          {status && <TicketStatusChip status={status} />}
          {(ticket?.category_name ?? summary?.category_name) && (
            <span>· {ticket?.category_name ?? summary?.category_name}</span>
          )}
        </div>
        <SheetTitle className="text-base leading-snug">{ticket?.title ?? summary?.title ?? 'Request'}</SheetTitle>
        <SheetDescription>
          {status ? TICKET_STATUS_META[status].hint : ''}
          {(ticket?.assignee_name ?? summary?.assignee_name) && (
            <> {ticket?.assignee_name ?? summary?.assignee_name} has it.</>
          )}
        </SheetDescription>
      </SheetHeader>

      <SheetBody className="space-y-4">
        {threadQ.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : threadQ.error ? (
          <p className="rounded-xl border border-fresh-salmon/60 bg-fresh-salmon/10 px-4 py-3 text-sm text-fresh-salmon-dark">
            {describeWorkError(threadQ.error)}
          </p>
        ) : (
          <>
            {ticket?.description && (
              <p className="whitespace-pre-wrap rounded-xl bg-secondary/40 px-4 py-3 text-sm text-foreground">
                {ticket.description}
              </p>
            )}

            {ticket?.resolution && status && isTicketClosed(status) && (
              <div className="rounded-xl border border-border bg-card px-4 py-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {status === 'declined' ? 'Why not' : 'What happened'}
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{ticket.resolution}</p>
              </div>
            )}

            <ol className="space-y-3">
              {items.map((item) =>
                item.kind === 'event' ? (
                  <li key={item.event.id} className="flex items-start gap-3 text-xs text-muted-foreground">
                    <span
                      aria-hidden
                      className={cn(
                        'ml-2 mt-1.5 h-2 w-2 shrink-0 rounded-full',
                        item.event.kind === 'status_changed' &&
                          item.event.to_value &&
                          item.event.to_value in TICKET_STATUS_META
                          ? TICKET_STATUS_META[item.event.to_value as TicketStatus].dot
                          : 'bg-border',
                      )}
                    />
                    <p className="min-w-0 flex-1 leading-5">
                      <span className="font-medium text-foreground/80">{item.event.actor_name ?? 'Atlas'}</span>{' '}
                      {describeTicketEvent(item.event)}
                      <span className="ml-2" title={formatDateTime(item.event.created_at)}>
                        {formatRelative(item.event.created_at)}
                      </span>
                    </p>
                  </li>
                ) : (
                  <li
                    key={item.comment.id}
                    className={cn('flex', item.comment.mine ? 'justify-end' : 'justify-start')}
                  >
                    <div
                      className={cn(
                        'max-w-[85%] rounded-2xl px-4 py-2.5',
                        item.comment.mine
                          ? 'rounded-br-md bg-primary text-primary-foreground'
                          : 'rounded-bl-md border border-border bg-card text-foreground',
                      )}
                    >
                      {!item.comment.mine && (
                        <p className="text-xs font-medium text-muted-foreground">{item.comment.author_name}</p>
                      )}
                      <p className="whitespace-pre-wrap text-sm">{item.comment.body}</p>
                      <p
                        className={cn(
                          'mt-1 text-[11px]',
                          item.comment.mine ? 'text-primary-foreground/70' : 'text-muted-foreground',
                        )}
                        title={formatDateTime(item.comment.created_at)}
                      >
                        {formatRelative(item.comment.created_at)}
                      </p>
                    </div>
                  </li>
                ),
              )}
            </ol>
          </>
        )}
      </SheetBody>

      <div className="border-t border-border px-6 py-4">
        <div className="flex items-end gap-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={
              status && isTicketClosed(status)
                ? 'Add a note (this request is closed)…'
                : 'Reply — “the 10 mm one, please”…'
            }
            className="min-h-[52px] flex-1 text-base"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                void send();
              }
            }}
          />
          <Button
            size="lg"
            className="h-12 w-12 shrink-0 p-0"
            onClick={() => void send()}
            disabled={reply.isPending || body.trim().length === 0}
            aria-label="Send reply"
          >
            <Send className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </>
  );
}
